import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PrivacyNote } from "@/components/PrivacyNote";
import { BookOpen, Mic, Square, Search, Trash2, Plus, Sparkles } from "lucide-react";

export default function Journal() {
  const { t } = useI18n();
  const [entries, setEntries] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const load = (query) => api.get("/journals", { params: query ? { q: query } : {} }).then(({ data }) => setEntries(data.journals || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!content.trim()) return toast.error("Write something first");
    setSaving(true);
    try {
      await api.post("/journals", { title, content, kind: "text", private: true });
      toast.success("Entry saved. I've noted the themes gently.");
      setOpen(false); setTitle(""); setContent(""); load(q);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const del = async (id) => { await api.delete(`/journals/${id}`); load(q); };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const form = new FormData();
        form.append("file", blob, "voice.webm");
        setTranscribing(true);
        try {
          const { data } = await api.post("/journals/transcribe", form, { headers: { "Content-Type": "multipart/form-data" } });
          setContent((c) => (c ? c + "\n" : "") + data.text);
          toast.success("Transcribed your voice note");
        } catch (e) { toast.error(apiErr(e)); } finally { setTranscribing(false); }
      };
      rec.start(); recRef.current = rec; setRecording(true);
    } catch { toast.error("Microphone access denied"); }
  };
  const stopRec = () => { recRef.current?.stop(); setRecording(false); };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold sm:text-4xl">{t("journal.title")}</h1>
          <p className="mt-2 text-muted-foreground">{t("journal.subtitle")}</p>
          <PrivacyNote className="mt-3" />
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full gap-2" data-testid="new-journal-button"><Plus className="h-4 w-4" strokeWidth={1.5} /> {t("journal.newEntry")}</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg rounded-3xl">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{t("journal.dialogTitle")}</DialogTitle><DialogDescription>{t("journal.dialogDesc")}</DialogDescription></DialogHeader>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("journal.titlePh")} data-testid="journal-title-input" className="rounded-xl" />
            <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder={t("journal.contentPh")}
                      data-testid="journal-content-input" className="min-h-48 resize-none rounded-2xl text-base leading-relaxed" />
            <div className="flex items-center justify-between">
              {!recording ? (
                <Button variant="outline" onClick={startRec} disabled={transcribing} className="rounded-full gap-2" data-testid="voice-record-button">
                  <Mic className="h-4 w-4" strokeWidth={1.5} /> {transcribing ? t("journal.transcribing") : t("journal.voiceNote")}
                </Button>
              ) : (
                <Button variant="destructive" onClick={stopRec} className="rounded-full gap-2 animate-breathe" data-testid="voice-stop-button">
                  <Square className="h-4 w-4" strokeWidth={1.5} /> {t("journal.stop")}
                </Button>
              )}
              <Button onClick={save} disabled={saving} className="rounded-full" data-testid="save-journal-button">{saving ? t("journal.transcribing") : t("journal.save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative mt-6 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
        <Input value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }} placeholder={t("journal.searchPh")}
               data-testid="journal-search-input" className="rounded-full pl-9" />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
        {entries.length === 0 && <p className="text-sm text-muted-foreground">{t("journal.empty")}</p>}
        {entries.map((e, i) => (
          <motion.div key={e.journal_id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.04 }}
                      className="ambient-shadow group rounded-3xl border border-border/60 bg-card p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-primary" strokeWidth={1.5} />
                <span className="font-serif text-lg">{e.title || "Untitled"}</span>
              </div>
              <button onClick={() => del(e.journal_id)} data-testid={`delete-journal-${e.journal_id}`}
                      className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"><Trash2 className="h-4 w-4" strokeWidth={1.5} /></button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</p>
            <p className="mt-3 line-clamp-4 text-sm leading-relaxed text-foreground/80">{e.content}</p>
            {e.summary && (
              <div className="mt-4 rounded-2xl bg-accent/40 p-3">
                <div className="flex items-center gap-1.5 text-xs font-medium text-primary"><Sparkles className="h-3 w-3" strokeWidth={1.5} /> {t("journal.reflection")}</div>
                <p className="mt-1 text-xs text-muted-foreground">{e.summary}</p>
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-1.5">
              {e.emotion && <Badge variant="secondary" className="rounded-full text-xs">{e.emotion}</Badge>}
              {(e.topics || []).map((t) => <Badge key={t} variant="outline" className="rounded-full text-xs">{t}</Badge>)}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
